import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizTakePage = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API_URL}/quizzes/${quizId}`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Failed to fetch quiz:', error);
      toast.error('Failed to load quiz');
      navigate('/quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const nextQuestion = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/quizzes/${quizId}/submit`, {
        quiz_id: quizId,
        answers
      });
      setCompleted(true);
      toast.success('Quiz completed! Your compatibility profile has been updated.');
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (completed) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto text-center py-16" data-testid="quiz-completed">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-serif font-semibold mb-4">Quiz Completed!</h1>
          <p className="text-muted-foreground mb-8">
            Your answers have been recorded and your compatibility profile has been updated.
            This will help us find better matches for you.
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/quizzes')}
              className="rounded-full"
            >
              More Quizzes
            </Button>
            <Button 
              onClick={() => navigate('/discover')}
              className="rounded-full"
            >
              Find Matches
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const question = quiz?.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz?.questions.length) * 100;
  const isLastQuestion = currentQuestion === quiz?.questions.length - 1;
  const currentAnswer = answers[question?.id];
  const allAnswered = quiz?.questions.every(q => answers[q.id]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="quiz-take-page">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/quizzes')}
            className="mb-4 -ml-2 rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Quizzes
          </Button>
          
          <h1 className="text-2xl font-serif font-semibold mb-2">{quiz?.title}</h1>
          
          <div className="flex items-center gap-4">
            <Progress value={progress} className="flex-1 h-2" />
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {currentQuestion + 1} / {quiz?.questions.length}
            </span>
          </div>
        </div>

        {/* Question Card */}
        <Card className="rounded-2xl border-border/50 shadow-soft">
          <CardContent className="p-8">
            <h2 className="text-xl font-medium mb-8">{question?.text}</h2>

            {/* Options */}
            <div className="space-y-3">
              {question?.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(question.id, option)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                    currentAnswer === option
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30 hover:bg-accent/30'
                  }`}
                  data-testid={`option-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      currentAnswer === option ? 'border-primary bg-primary' : 'border-muted-foreground'
                    }`}>
                      {currentAnswer === option && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    <span>{option}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
            className="rounded-full"
            data-testid="prev-question-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="rounded-full"
              data-testid="submit-quiz-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Complete Quiz
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              disabled={!currentAnswer}
              className="rounded-full"
              data-testid="next-question-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QuizTakePage;
